/**
 * ByMarca.jsx — Vista detalhada por marca.
 */

import React, { useEffect, useState } from "react";
import { formatDate } from "../utils/formatDate";
import { fetchMarcas, fetchMarca } from "../services/api";
import SearchableSelect from "./SearchableSelect";

const BUCKETS_ORDEM = [
  "ROADMAP","NEGOCIAR DEADLINE","ONGOING",
  "AGUARDO FEEDBACK","PARA APROVAÇÃO INTERNA",
  "PARA APROVAÇÃO EXTERNA","APROVADO","CONCLUÍDO",
];

export default function ByMarca({ marcaInicial = null, onClear = null }) {
  const [marcas, setMarcas]               = useState([]);
  const [marcaSelecionada, setMarcaSel]   = useState(marcaInicial || "");
  const [data, setData]                   = useState(null);
  const [loadingLista, setLoadingLista]   = useState(true);
  const [loadingDados, setLoadingDados]   = useState(false);
  const [erro, setErro]                   = useState(null);

  useEffect(() => {
    fetchMarcas()
      .then((lista) => {
        setMarcas(lista);
        if (marcaInicial) handleSeleccao(marcaInicial);
        else if (lista.length === 1) handleSeleccao(lista[0].plan_id);
      })
      .catch((e) => setErro(e.message))
      .finally(() => setLoadingLista(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSeleccao = (planId) => {
    setMarcaSel(planId);
    if (!planId) { setData(null); return; }
    setLoadingDados(true);
    setErro(null);
    setData(null);
    fetchMarca(planId)
      .then(setData)
      .catch((e) => setErro(e.message))
      .finally(() => setLoadingDados(false));
  };

  return (
    <div>
      <h1 style={s.titulo}>Análise de Marca</h1>

      <div style={s.selectorBar}>
        {loadingLista ? <p style={s.loading}>A carregar…</p> : (
          <SearchableSelect
            options={marcas.map((m) => ({ value: m.plan_id, label: m.plan_title }))}
            value={marcaSelecionada}
            onChange={handleSeleccao}
            placeholder="Seleccionar marca…"
          />
        )}
      </div>

      {erro && <div style={s.erro}>{erro}</div>}
      {loadingDados && <p style={s.loading}>A carregar dados…</p>}

      {data && (
        <>
          {/* Cabeçalho */}
          <h2 style={s.marcaNome}>{data.marca_nome}</h2>

          {/* Score + KPIs lado a lado */}
          <div className="grid-topo-score" style={s.topoGrid}>
            <ScorePanel
              score={data.score_saude}
              componentes={data.score_componentes}
              pesos={data.score_pesos}
            />
            <div style={s.kpiGrid}>
              <KpiCard label="Tarefas Ativas"      valor={data.kpis.tarefas_ativas}       cor="#6366f1" />
              <KpiCard label="Deadline Próximo"      valor={data.kpis.n_deadline_proximo}   cor="#f97316"
                desc="Hoje, amanhã ou depois de amanhã" />
              <KpiCard label="Feedback Externo (avg)" valor={`${data.kpis.tempo_medio_feedback_externo}d`} cor="#f97316" />
              <KpiCard label="Ritmo"
                valor={`${data.kpis.ritmo_variacao_pct > 0 ? "+" : ""}${data.kpis.ritmo_variacao_pct}%`}
                cor={data.kpis.ritmo_variacao_pct >= 0 ? "#22c55e" : "#ef4444"} />
            </div>
          </div>

          {/* Prazo Ultrapassado */}
          <div style={s.section}>
            <h3 style={s.sectionTitulo}>
              Prazo Ultrapassado
              {data.tarefas_ultrapassadas.length > 0 && (
                <span style={s.countBadgeRed}>{data.tarefas_ultrapassadas.length}</span>
              )}
            </h3>
            {data.tarefas_ultrapassadas.length === 0 ? (
              <div style={s.okBox}>
                <span>✅</span>
                <span style={s.okText}>Sem tarefas com prazo ultrapassado.</span>
              </div>
            ) : (
              <div className="table-scroll">
              <table style={s.tabela}>
                <thead>
                  <tr>
                    <th style={s.th}>Tarefa</th>
                    <th style={s.th}>Bucket</th>
                    <th style={s.th}>Deadline</th>
                  </tr>
                </thead>
                <tbody>
                  {data.tarefas_ultrapassadas.map((t) => (
                    <tr key={t.task_id} style={s.trUltra}>
                      <td style={s.td}>{t.title}</td>
                      <td style={s.td}><span style={s.bucketTag}>{t.bucket_name}</span></td>
                      <td style={{ ...s.td, fontWeight: 500, color: "#dc2626" }}>{formatDate(t.due_date)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            )}
          </div>

          {/* Deadline Próximo */}
          <div style={s.section}>
            <h3 style={s.sectionTitulo}>
              Deadline Próximo
              {data.tarefas_deadline_proximo.length > 0 && (
                <span style={s.countBadge}>{data.tarefas_deadline_proximo.length}</span>
              )}
            </h3>
            {data.tarefas_deadline_proximo.length === 0 ? (
              <div style={s.okBox}>
                <span>✅</span>
                <span style={s.okText}>Sem tarefas com deadline próximo.</span>
              </div>
            ) : (
              <div className="table-scroll">
              <table style={s.tabela}>
                <thead>
                  <tr>
                    <th style={s.th}>Tarefa</th>
                    <th style={s.th}>Bucket</th>
                    <th style={s.th}>Deadline</th>
                  </tr>
                </thead>
                <tbody>
                  {data.tarefas_deadline_proximo.map((t) => (
                    <tr key={t.task_id} style={s.tr}>
                      <td style={s.td}>{t.title}</td>
                      <td style={s.td}><span style={s.bucketTag}>{t.bucket_name}</span></td>
                      <td style={{ ...s.td, fontWeight: 500, color: "#ea580c" }}>{formatDate(t.due_date)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            )}
          </div>

          {/* Pipeline */}
          {Object.keys(data.pipeline).length > 0 && (
            <div style={s.section}>
              <h3 style={s.sectionTitulo}>Pipeline</h3>
              <div className="grid-pipeline" style={s.pipelineGrid}>
                {BUCKETS_ORDEM.map((b) => (
                  <div key={b} style={s.bucketCard}>
                    <span style={s.bucketNome}>{b}</span>
                    <span style={s.bucketTotal}>{data.pipeline[b] ?? 0}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Score Panel — score + barras de componentes
// ---------------------------------------------------------------------------

function ScorePanel({ score, componentes, pesos }) {
  const cor = score >= 70 ? "#22c55e" : score >= 40 ? "#f97316" : "#ef4444";

  const itens = [
    { key: "risco",         label: "Deadlines em dia",       desc: "100 = sem prazos ultrapassados" },
    { key: "aprov_externa", label: "Velocidade de aprovação",desc: "100 = aprovação dentro do prazo" },
    { key: "aguardo_info",  label: "Velocidade de feedback", desc: "100 = sem espera de informação" },
    { key: "ritmo",         label: "Ritmo de conclusão",     desc: "100 = ritmo igual ou melhor" },
  ];

  return (
    <div style={s.scorePanel}>
      {/* Número grande */}
      <div style={s.scoreTopo}>
        <div style={{ ...s.scoreCirculo, borderColor: cor }}>
          <span style={{ ...s.scoreNum, color: cor }}>{score}</span>
        </div>
        <div>
          <div style={s.scoreTitulo}>Score de Saúde</div>
          <div style={{ ...s.scoreClassif, color: cor }}>
            {score >= 70 ? "Saudável" : score >= 40 ? "Atenção" : "Crítico"}
          </div>
        </div>
      </div>

      {/* Componentes */}
      <div style={s.scoreComponentes}>
        {itens.map(({ key, label, desc }) => {
          const val    = componentes?.[key] ?? 0;
          const peso   = pesos?.[key] ?? 0;
          const barCor = val >= 70 ? "#22c55e" : val >= 40 ? "#f97316" : "#ef4444";
          return (
            <div key={key} style={s.scoreItem}>
              <div style={s.scoreItemHeader}>
                <div style={{ flex: 1 }}>
                  <span style={s.scoreItemLabel}>{label}</span>
                  <span style={s.scoreItemDesc}>{desc}</span>
                </div>
                <span style={{ ...s.scoreItemVal, color: barCor }}>{val}<span style={s.scoreItemMax}>/100</span></span>
                <span style={s.scoreItemPeso}>peso {peso}%</span>
              </div>
              <div style={s.barraFundo}>
                <div style={{ ...s.barraPreench, width: `${val}%`, backgroundColor: barCor }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function KpiCard({ label, valor, cor, desc }) {
  return (
    <div style={{ ...s.kpiCard, borderTop: `4px solid ${cor}` }}>
      <span style={{ ...s.kpiValor, color: cor }}>{valor}</span>
      <span style={s.kpiLabel}>{label}</span>
      {desc && <span style={s.kpiDesc}>{desc}</span>}
    </div>
  );
}

// ---------------------------------------------------------------------------
const s = {
  titulo:    { fontSize: 24, fontWeight: 700, marginBottom: 20 },
  marcaNome: { fontSize: 20, fontWeight: 700, margin: "0 0 20px", color: "#1e293b" },
  selectorBar: { marginBottom: 20 },

  topoGrid: { display: "grid", gridTemplateColumns: "320px 1fr", gap: 20, marginBottom: 20, alignItems: "start" },

  // Score panel
  scorePanel: { background: "#fff", borderRadius: 8, padding: 20, boxShadow: "0 1px 3px rgba(0,0,0,.08)" },
  scoreTopo:  { display: "flex", alignItems: "center", gap: 16, marginBottom: 20 },
  scoreCirculo: { width: 72, height: 72, borderRadius: "50%", border: "4px solid", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
  scoreNum:   { fontSize: 24, fontWeight: 800 },
  scoreTitulo:{ fontSize: 13, fontWeight: 600, color: "#374151" },
  scoreClassif:{ fontSize: 12, fontWeight: 700, marginTop: 2 },
  scoreComponentes: { display: "flex", flexDirection: "column", gap: 10 },
  scoreItem:  { display: "flex", flexDirection: "column", gap: 4 },
  scoreItemHeader: { display: "flex", alignItems: "center", gap: 6 },
  scoreItemLabel: { display: "block", fontSize: 12, fontWeight: 600, color: "#374151" },
  scoreItemDesc:  { display: "block", fontSize: 10, color: "#94a3b8", marginTop: 1 },
  scoreItemVal:   { fontSize: 13, fontWeight: 700, minWidth: 48, textAlign: "right", flexShrink: 0 },
  scoreItemMax:   { fontSize: 10, color: "#94a3b8", fontWeight: 400 },
  scoreItemPeso:  { fontSize: 10, color: "#94a3b8", minWidth: 52, textAlign: "right", flexShrink: 0 },
  barraFundo: { height: 6, backgroundColor: "#f1f5f9", borderRadius: 3 },
  barraPreench: { height: 6, borderRadius: 3, transition: "width 0.4s" },

  // KPIs
  kpiGrid: { display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 12 },
  kpiCard: { background: "#fff", borderRadius: 8, padding: "16px 20px", boxShadow: "0 1px 3px rgba(0,0,0,.08)", display: "flex", flexDirection: "column", gap: 4 },
  kpiValor: { fontSize: 28, fontWeight: 700, lineHeight: 1 },
  kpiLabel: { fontSize: 13, fontWeight: 600, color: "#374151" },
  kpiDesc:  { fontSize: 11, color: "#94a3b8" },

  // Sections
  section: { background: "#fff", borderRadius: 8, padding: 20, boxShadow: "0 1px 3px rgba(0,0,0,.08)", marginBottom: 16 },
  sectionTitulo: { fontSize: 15, fontWeight: 600, margin: "0 0 14px", color: "#1e293b", display: "flex", alignItems: "center", gap: 8 },
  countBadge:    { background: "#ffedd5", color: "#ea580c", borderRadius: 10, padding: "1px 8px", fontSize: 12, fontWeight: 700 },
  countBadgeRed: { background: "#fee2e2", color: "#dc2626", borderRadius: 10, padding: "1px 8px", fontSize: 12, fontWeight: 700 },
  okBox:  { display: "flex", alignItems: "center", gap: 8, color: "#16a34a", fontSize: 14 },
  okText: { color: "#16a34a" },

  // Tarefas em risco table
  tabela: { width: "100%", borderCollapse: "collapse", fontSize: 13 },
  th:     { textAlign: "left", padding: "7px 12px", backgroundColor: "#f8fafc", fontWeight: 600, borderBottom: "2px solid #e2e8f0", color: "#475569" },
  tr:     { borderBottom: "1px solid #f1f5f9" },
  trUltra:{ borderBottom: "1px solid #f1f5f9", backgroundColor: "#fff5f5" },
  td:     { padding: "8px 12px", color: "#374151" },
  bucketTag: { display: "inline-block", padding: "2px 8px", background: "#f1f5f9", color: "#475569", borderRadius: 4, fontSize: 11, fontWeight: 500 },
  badgeUltra: { display: "inline-block", background: "#fee2e2", color: "#dc2626", borderRadius: 10, padding: "2px 8px", fontSize: 11, fontWeight: 700 },
  badgeRisco: { display: "inline-block", background: "#ffedd5", color: "#ea580c", borderRadius: 10, padding: "2px 8px", fontSize: 11, fontWeight: 700 },

  // Pipeline
  pipelineGrid: { display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10 },
  bucketCard:   { border: "1px solid #e2e8f0", borderRadius: 6, padding: "10px 14px", display: "flex", flexDirection: "column", gap: 2 },
  bucketNome:   { fontSize: 10, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.04em" },
  bucketTotal:  { fontSize: 20, fontWeight: 700, color: "#1e293b" },

  loading: { color: "#64748b", fontSize: 14 },
  erro:    { background: "#fef2f2", border: "1px solid #fca5a5", borderRadius: 6, padding: 16, color: "#dc2626", marginBottom: 16 },
};
